import type { UIMessage } from "@ai-sdk/react";
import type {
  ServerMessage,
  ServerPart,
  ServerToolCallPart,
  ServerToolResultPart,
} from "../types";

type UIMessagePart = UIMessage["parts"][number];

// Explicit types for tool UI parts matching AI SDK structure
// These are more specific than the generic UIMessagePart union
interface ToolUIPartBase {
  type: `tool-${string}`;
  toolCallId: string;
  input: unknown;
}

interface ToolUIPartOutputAvailable extends ToolUIPartBase {
  state: "output-available";
  output: unknown;
  errorText?: never;
}

interface ToolUIPartOutputError extends ToolUIPartBase {
  state: "output-error";
  output?: never;
  errorText: string;
}

type ToolPart = ToolUIPartOutputAvailable | ToolUIPartOutputError;

export function mapServerMessagesToUI(
  serverMessages: ServerMessage[]
): UIMessage[] {
  // API returns DESC order (newest first), UI needs ASC (oldest first)
  const reversed = [...serverMessages].reverse();
  return reversed.map(mapServerMessageToUI);
}

function mapServerMessageToUI(msg: ServerMessage): UIMessage {
  return {
    id: msg.id,
    role: msg.role,
    parts: mapServerPartsToUI(msg.parts),
  };
}

function mapServerPartsToUI(parts: ServerPart[]): UIMessagePart[] {
  const uiParts: UIMessagePart[] = [];

  // Build a map of toolCallId -> tool-result for quick lookup
  const toolResults = new Map<string, ServerToolResultPart>();
  for (const part of parts) {
    if (part.type === "tool-result") {
      toolResults.set(part.toolCallId, part);
    }
  }

  for (const part of parts) {
    if (part.type === "text") {
      uiParts.push({
        type: "text",
        text: part.content,
      });
    } else if (part.type === "tool-call") {
      const toolCall = part as ServerToolCallPart;
      const result = toolResults.get(toolCall.id);

      // Create tool part with type "tool-{toolName}"
      const toolPart = createToolUIPart(toolCall, result);
      // Cast is safe: ToolPart is a subset of UIMessagePart's tool variants
      uiParts.push(toolPart as UIMessagePart);
    }
    // Skip tool-result - they're attached to tool-call parts above
  }

  return uiParts;
}

function createToolUIPart(
  toolCall: ServerToolCallPart,
  result?: ServerToolResultPart
): ToolPart {
  const hasError = result?.error != null && result.error !== "";

  const base: ToolUIPartBase = {
    type: `tool-${toolCall.name}`,
    toolCallId: toolCall.id,
    input: toolCall.arguments,
  };

  if (hasError) {
    return {
      ...base,
      state: "output-error",
      errorText: result!.error!,
    };
  }

  return {
    ...base,
    state: "output-available",
    output: result?.output ? parseToolOutput(result.output) : undefined,
  };
}

function parseToolOutput(output: string): unknown {
  try {
    return JSON.parse(output);
  } catch {
    return output;
  }
}
